<template>
  <div :class="{ fixed: navShown }" class="overflow-hidden leading-relative">
    <Highlight />
    <Header />
    <main>
      <Nuxt />
    </main>
    <client-only>
      <noscript>
        <iframe
          :src="`https://www.googletagmanager.com/ns.html?id=${gtmKey}&noscript=`"
          height="0"
          width="0"
          style="display: none; visibility: hidden"
        ></iframe>
      </noscript>
    </client-only>
    <Footer />
    <noscript v-html="cheqTag.noScript" />
  </div>
</template>
<script>
import Header from '~/components/header/Header.vue';
import Footer from '~/components/core/shared/footer/Footer.vue';
import Highlight from '~/components/Highlight.vue';
import { getCookie, nginxDecodeUserId } from '~/helpers/cookieHelpers';
// Language mapping for special domains with alternative languages
const specialDomainLanguages = {
  'de-ch.parkos.com': 'dv', // Divehi
  'eu.parkos.com': 'eo', // Esperanto
  'fr-ch.parkos.com': 'fur', // Friulian
  'fr.parkos.be': 'ff', // Fulah
  'it-ch.parkos.com': 'smn', // Inari Sami
  'parkos.com.au': 'ebu', // Embu
  'parkos.co.uk': 'ee', // Ewe
};

export default {
  components: {
    Header,
    Footer,
    Highlight,
  },
  data() {
    return {
      navShown: false,
      gtmKey: undefined,
    };
  },
  head() {
    const script = [];

    if (this.cheqTag) {
      script.push(this.cheqTag.script);
    }

    script.push({
      id: 'Cookiebot',
      src: 'https://consent.cookiebot.com/uc.js',
      'data-cbid': 'ea7b04b3-d681-4591-99a4-50bdcf5fa1d5',
      'data-culture': this.getCookiebotLanguage(this.language.domain, this.language.lang),

      type: 'text/javascript',
      async: true,
    });

    return { script };
  },
  computed: {
    language() {
      return this.$store.state.language;
    },
    cheqTag() {
      return this.$cheq(this.language.domain);
    },
  },
  mounted() {
    this.loadGTM();

    window.addEventListener('CookiebotOnDialogDisplay', () => {
      window.dataLayer.push({
        event: 'experiment_data',
        dl_experiment_id: 'BE-1356',
        dl_experiment_action: 'impression',
        dl_experiment_value: 'banner',
      });
      const configure = document.querySelector('#CybotCookiebotDialogBodyLevelButtonLevelOptinAllowallSelection');
      const selectAll = document.querySelector('#CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll');

      setTimeout(() => {
        selectAll?.addEventListener('click', () => {
          setTimeout(() => {
            window.dataLayer.push({
              event: 'experiment_data',
              dl_experiment_id: 'BE-1356',
              dl_experiment_action: 'selection',
              dl_experiment_value: 'accept_all',
            });
            this.sendEvent('accept_all', 'Action Tracking', 'cookieBanner');
          });
        });

        configure?.addEventListener('click', () => {
          setTimeout(() => {
            window.dataLayer.push({
              event: 'experiment_data',
              dl_experiment_id: 'BE-1356',
              dl_experiment_action: 'selection',
              dl_experiment_value: 'accept_selection',
            });
            this.sendEvent('accept_selection', 'Action Tracking', 'cookieBanner');
          });
        });
      });
      window.addEventListener(
        'CookiebotOnDecline',
        () => {
          if (window.Cookiebot.changed) {
            window.dataLayer.push({
              event: 'experiment_data',
              dl_experiment_id: 'BE-1356',
              dl_experiment_action: 'selection',
              dl_experiment_value: 'decline_all',
            });
            this.sendEvent('decline_all', 'Action Tracking', 'cookieBanner');
          }
        },
        false,
      );
    });
  },
  created() {
    this.gtmKey = this.language?.gtm_key;
  },
  methods: {
    loadGTM() {
      const { gtmKey } = this;

      if (gtmKey) {
        /* eslint-disable */
        // @ts-ignore
        window.loadTagManager = function (w, d, s, l) {
          w[l] = w[l] || [];
          w[l].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
          });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = `https://www.googletagmanager.com/gtm.js?id=${gtmKey}` + dl;
          f.parentNode.insertBefore(j, f);
        };
        if (typeof tagManagerDelay === 'undefined') {
          window.loadTagManager(window, document, 'script', 'dataLayer');
        }
        /* eslint-enable */
      }
    },
    navToggle(value) {
      this.navShown = value;
    },
    // Enhanced method to get the appropriate language for Cookiebot
    getCookiebotLanguage(currentDomain, currentLanguage) {
      // Check if the current domain needs a special language
      if (specialDomainLanguages[currentDomain]) {
        return specialDomainLanguages[currentDomain];
      }

      // Use the existing removeExtractLanguage method
      return this.removeExtractLanguage(currentLanguage);
    },

    // Existing method to remove language region
    removeExtractLanguage(languageCode) {
      const parts = languageCode.split('-');

      if (parts.length === 1) {
        return languageCode;
      }
      return parts[0];
    },
    async sendEvent(ea, ec, el) {
      const { host } = window.location;
      const pageType = () => window.$nuxt?.$store?.state?.bucket?.page_type || window.pageType;

      const visitorIdCookie = getCookie('uid');
      if (!visitorIdCookie) {
        console.log('[FLAGSHIP GTM] VisitorId is not defined');
        return;
      }
      const visitorId = nginxDecodeUserId(visitorIdCookie);
      const flagshipEnvironmentId = this.$flagshipEnvironmentId;
      const flagshipEventsAPIEndpoint = this.$config.FLAGSHIP_EVENTS_API_BASE_URL;

      const response = await fetch(`${flagshipEventsAPIEndpoint}?host=${host}&app=${pageType()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cid: flagshipEnvironmentId,
          vid: visitorId,
          dl: document.location.href,
          ds: 'APP',
          ev: 1,
          t: 'EVENT',
          ea,
          ec,
          el,
        }),
      });
      if (response.status !== 200) {
        console.error(`[FLAGSHIP GTM] Event could not be be sent, HTTP ${response.status}`);
      }
    },
  },
};
</script>

<style>
* {
  -webkit-font-smoothing: antialiased;
}

html {
  scroll-behavior: smooth;
}

.skew {
  transform: skew(0deg, -1deg);
}

.unskew {
  transform: skew(0deg, 1deg);
}

.skew,
.unskew {
  backface-visibility: hidden;
}

.caret {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 2px;
  vertical-align: middle;
  border-top: 4px dashed;
  border-top: 4px solid \9;
  border-right: 4px solid transparent;
  border-left: 4px solid transparent;
}
</style>
